import { useEffect, useRef, useState } from "react";
import User from "../../../models/User";
import AuthService from "../../../services/AuthService";
import Logger from "../../../utils/Logger";
import { proxyStorage } from "../../../utils/proxyStorage";
import { LocalRefreshTokenKey, LocalTokenExpireKey, storeLocalUserInfo, loadLocalUserInfo } from "./useAuthentication";

const Log = Logger.getLogger("useRefreshToken");

const ExpireTrackingInterval = 10000;

export default function useRefreshToken(props: {
	onRefreshTokenSuccess: (nToken: string | null, nUserInfo: User | null) => void;
	onRefreshTokenFail: () => void;
	getSessionEpoch?: () => number;
}) {
	const [refreshToken, setRefreshToken] = useState(() => proxyStorage()?.getItem(LocalRefreshTokenKey) || null);
	const [tokenExpireTime, setTokenExpireTime] = useState(() => loadLocalTokenExpireTime());

	const refreshTokenRef = useRef(refreshToken);
	const tokenExpireTimeRef = useRef(tokenExpireTime);

	const expireTrackingJobRef = useRef<NodeJS.Timeout>();

	const handleLoginSuccess = (uInfo: User) => {
		Log.log(`begin handleLoginSuccess`);
		setRefreshToken(uInfo.refreshToken);
		setTokenExpireTime(uInfo.expireTime);
		localStorage.setItem(LocalRefreshTokenKey, uInfo.refreshToken || "");
		storeLocalTokenExpireTime(uInfo.expireTime);

		restartExpireTrackingJob();
	};

	const onValidateFail = async () => {
		const epochAtStart = props.getSessionEpoch?.() ?? 0;
		doRefreshToken(
			(user) => {
				if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
				restartExpireTrackingJob();
				props?.onRefreshTokenSuccess(user.token, user);
			},
			() => {
				if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
				props?.onRefreshTokenFail();
			}
		);
	};

	const onValidateSuccess = async () => {
		restartExpireTrackingJob();
	};

	const doRefreshToken = async (onSuccess: (user: User) => void, onFail?: () => void) => {
		const epochAtStart = props.getSessionEpoch?.() ?? 0;

		if (!refreshTokenRef.current) {
			if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
			onFail?.();
			return;
		}

		try {
			let user = await AuthService.refreshToken(refreshTokenRef.current || "");

			if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;

			setTokenExpireTime(user.expireTime);
			storeLocalTokenExpireTime(user.expireTime);

			try {
				const data = await AuthService.getUserInfo(user.token || "");
				if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
				const fullUser = new User().parse(data);
				fullUser.token = user.token;
				fullUser.refreshToken = user.refreshToken;
				fullUser.expireTime = user.expireTime;

				// Cập nhật localStorage với user info mới
				storeLocalUserInfo(fullUser);

				onSuccess(fullUser);
			} catch (meErr) {
				console.error("❌ Failed to get user info after refresh:", meErr);
				if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
				onSuccess(user);
			}
		} catch (err) {
			console.error(err);
			if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
			onFail?.();
		}
	};

	const restartExpireTrackingJob = () => {
		if (expireTrackingJobRef.current) {
			clearInterval(expireTrackingJobRef.current);
		}

		expireTrackingJobRef.current = setInterval(expireTracking, ExpireTrackingInterval);
	};

	const expireTracking = () => {
		if (!tokenExpireTimeRef.current) return;

		let current = new Date();
		let timeUntilExpiry = tokenExpireTimeRef.current.getTime() - current.getTime();

		//if token will be expired soon
		if (timeUntilExpiry <= ExpireTrackingInterval) {
			const epochAtStart = props.getSessionEpoch?.() ?? 0;
			doRefreshToken((user) => {
				if ((props.getSessionEpoch?.() ?? 0) !== epochAtStart) return;
				props?.onRefreshTokenSuccess(user.token, user);
				restartExpireTrackingJob();
			});
		}
	};

	useEffect(() => {
		refreshTokenRef.current = refreshToken;
		tokenExpireTimeRef.current = tokenExpireTime;
	});

	return {
		handleLoginSuccess,
		onValidateFail,
		onValidateSuccess,
	};
}

export function loadLocalTokenExpireTime(): Date | null {
	try {
		if (!proxyStorage()?.getItem(LocalTokenExpireKey)) return null;

		let timestamp = parseInt(proxyStorage()?.getItem(LocalTokenExpireKey) || "");
		return new Date(timestamp);
	} catch (err) {
		console.error(err);
	}

	return null;
}

export function storeLocalTokenExpireTime(expireTime: Date | null) {
	if (!expireTime) return;

	localStorage.setItem(LocalTokenExpireKey, expireTime.getTime() + "");
}
